import { create } from 'zustand'

import { FolderWithParent } from '@pages/my-folders/my-folders-utils'

export type Project = {
  id: string
  name: string
  ownerId: string
  owner?: {
    id: string
    email: string
  }
  access: {
    userId: string
    write: boolean
    download: boolean
  }[]
}

type OnUpdate = (project?: Project) => void | Promise<unknown>

export type OnCloseSaveDialog = (
  arg: { project: FolderWithParent; reason: 'confirm' } | { project: null; reason: 'cancel' },
) => void | Promise<unknown>

type State = {
  edit: {
    open: boolean
    onUpdate?: OnUpdate
  }

  save: {
    open: boolean
    mode: 'save' | 'move'
    selectedProjectId: string | null
    currentProjectId?: string | null
    onClose?: OnCloseSaveDialog
  }
}

type Actions = {
  openCreateProject: (onUpdate: OnUpdate) => void
  onCloseProjectEdit: () => void

  openSaveItem: (onClose: OnCloseSaveDialog, initialValue?: string) => void
  openMoveItem: (onClose: OnCloseSaveDialog, currentProject?: string) => void
  onCloseSaveItem: () => void

  reset: () => void
}

const initialState: State = {
  edit: {
    open: false,
  },

  save: {
    open: false,
    mode: 'save',
    selectedProjectId: null,
    currentProjectId: null,
  },
}

export const useProjectsState = create<State & Actions>((set) => {
  return {
    ...initialState,

    reset() {
      set({ ...initialState })
    },

    openCreateProject(onUpdate) {
      set({
        edit: {
          open: true,
          onUpdate,
        },
      })
    },

    onCloseProjectEdit() {
      set(() => ({
        edit: {
          ...initialState.edit,
        },
      }))
    },

    openSaveItem(onClose, initialValue) {
      set(({ save }) => ({
        save: {
          ...initialState.save,
          open: true,
          onClose,
          mode: 'save',
          selectedProjectId: initialValue || save.selectedProjectId,
        },
      }))
    },

    openMoveItem(onClose, currentProjectId) {
      set(() => ({
        save: {
          ...initialState.save,
          open: true,
          onClose,
          mode: 'move',
          currentProjectId,
        },
      }))
    },

    onCloseSaveItem() {
      set(({ save }) => ({
        save: {
          ...initialState.save,
          // should remember the selected project id
          selectedProjectId: save.selectedProjectId,
          mode: save.mode,
          open: false,
        },
      }))
    },
  }
})
